import { z } from 'zod';
import { IsoDateTimeSchema, UuidSchema } from './common';

/**
 * Jogosultságok (grant): ki olvashat egy adott publikációt.
 * A grant összeköti a felhasználót a publikációval — ki és mikor adta.
 */

/** Egy felhasználó, akinek jogosultsága van a publikációra. */
export const GrantedUserSchema = z.object({
  userId: UuidSchema,
  grantedBy: UuidSchema,
  grantedAt: IsoDateTimeSchema,
});
export type GrantedUser = z.infer<typeof GrantedUserSchema>;

export const GrantedUserListSchema = z.object({
  data: z.array(GrantedUserSchema),
});
export type GrantedUserList = z.infer<typeof GrantedUserListSchema>;

/** Egy publikáció, amelyre egy felhasználónak jogosultsága van. */
export const GrantedPublicationSchema = z.object({
  publicationId: UuidSchema,
  title: z.string().min(1),
  grantedAt: IsoDateTimeSchema,
});
export type GrantedPublication = z.infer<typeof GrantedPublicationSchema>;

export const GrantedPublicationListSchema = z.object({
  data: z.array(GrantedPublicationSchema),
});
export type GrantedPublicationList = z.infer<typeof GrantedPublicationListSchema>;

/** Lehetséges jelölt, akinek jogosultság adható (felhasználó-kereső találat). */
export const GrantCandidateSchema = z.object({
  id: UuidSchema,
  displayName: z.string(),
  email: z.string(),
});
export type GrantCandidate = z.infer<typeof GrantCandidateSchema>;

export const GrantCandidateListSchema = z.object({
  data: z.array(GrantCandidateSchema),
});
export type GrantCandidateList = z.infer<typeof GrantCandidateListSchema>;

/** Jogosultság-adás: egy publikációra egy vagy több felhasználónak. */
export const CreateGrantSchema = z.object({
  publicationId: UuidSchema,
  userIds: z.array(UuidSchema).min(1),
});
export type CreateGrantInput = z.infer<typeof CreateGrantSchema>;

/** Jogosultság-visszavonás: egy publikációról egy felhasználótól. */
export const RevokeGrantSchema = z.object({
  publicationId: UuidSchema,
  userId: UuidSchema,
});
export type RevokeGrantInput = z.infer<typeof RevokeGrantSchema>;

/**
 * Id → aktív-jogosultság-darabszám térkép (badge-szám).
 * A kulcs publikáció- vagy felhasználó-id; a 0-ás (grant nélküli) id-k hiányoznak
 * a térképből, a hívó tölti ki őket nullára.
 */
export const GrantCountMapSchema = z.object({
  counts: z.record(z.string(), z.number().int().nonnegative()),
});
export type GrantCountMap = z.infer<typeof GrantCountMapSchema>;
